import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import DynamicContactField from './DynamicContactField';
import { validateEmail, validatePhoneNumber } from '../../../utils/validations';
import messages from './messages';

const AddContactButton = ({ onClick, message, disabled }) => (
  <a
    className="btn-text"
    onClick={disabled ? undefined : onClick}
    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
  >
    <FormattedMessage {...message} />
  </a>
);

const ContactFieldArray = ({
  fields,
  type, // 'email' or 'phone'
  typeOptions,
  maxFields,
  defaultEntry,
  disabled,
  intl,
}) => {
  const isEmail = type === 'email';

  // Memoized validation functions to prevent re-renders
  const validateRequired = useMemo(() => {
    return (value) => {
      if (!value) {
        return intl.formatMessage({
          id: 'App.Form.Required',
          defaultMessage: 'Required',
        });
      }
      return undefined;
    };
  }, [intl]);

  const validateContact = useMemo(() => {
    return (value) => {
      if (!value) return undefined;

      const isValid = isEmail
        ? validateEmail(value)
        : validatePhoneNumber(value);
      if (!isValid) {
        return intl.formatMessage(
          isEmail ? messages.invalidEmail : messages.invalidPhoneNumber,
        );
      }
      return undefined;
    };
  }, [isEmail, intl]);

  const handleAdd = useCallback(() => {
    if (fields.length < maxFields) {
      fields.push({
        ...defaultEntry,
        isPrimary: fields.length === 0,
      });
    }
  }, [fields, maxFields, defaultEntry]);

  const handleRemove = useCallback(
    (index) => {
      fields.remove(index);
    },
    [fields],
  );

  // Ensure we always have at least one field (primary)
  if (fields.length === 0) {
    fields.push({ ...defaultEntry, isPrimary: true });
  } else if (fields.length > 0 && !fields.get(0).isPrimary) {
    // Ensure the first field is always marked as primary
    const firstField = fields.get(0);
    fields.remove(0);
    fields.insert(0, { ...firstField, isPrimary: true });
  }

  return (
    <div>
      {fields.map((field, index) => {
        const placeholderText =
          index === 0
            ? `Primary ${isEmail ? 'Email' : 'Phone'}`
            : index === 1
            ? `Secondary ${isEmail ? 'Email' : 'Phone'}`
            : `Tertiary ${isEmail ? 'Email' : 'Phone'}`;

        return (
          <DynamicContactField
            key={index}
            fieldName={field}
            index={index}
            type={type}
            typeOptions={typeOptions}
            onRemove={handleRemove}
            disabled={disabled}
            placeholder={placeholderText}
            validate={
              index === 0
                ? [validateRequired, validateContact]
                : [validateContact]
            }
            isPrimary={index === 0}
            isLast={index === fields.length - 1}
            label={intl.formatMessage(
              isEmail ? messages.emailLabel : messages.phoneLabel,
            )}
            typeLabel={intl.formatMessage(messages.fieldTypeLabel)}
          />
        );
      })}
      {fields.length < maxFields && (
        <AddContactButton
          onClick={handleAdd}
          message={messages[isEmail ? 'addEmail' : 'addPhone']}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default ContactFieldArray;
